// ============================================================
// Session boundary — everything cached for one signed-in account is
// dropped when the account changes, so a second login in the same tab
// never sees the previous account's projects, reports or selection.
// ============================================================

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'

import { apiSlice } from '@/apis/apiSlice'
import { portalApi } from '@/apis/portalApi'
import { v2Api } from '@/apis/v2Api'
import { logout, setCredentials, type AuthState } from '@/modules/auth/authSlice'
import { portalSignedIn, portalSignedOut } from '@/modules/portal/portalSessionSlice'

import { clearProject } from './projectSlice'

/** Same person in the same organization (e.g. a re-login) keeps the project selection. */
export function isSameSession(
    prev: Pick<AuthState, 'user' | 'tenant'>,
    next: Pick<AuthState, 'user' | 'tenant'>,
): boolean {
    return (
        !!prev.user &&
        !!prev.tenant &&
        prev.user.id === next.user?.id &&
        prev.tenant.id === next.tenant?.id
    )
}

export const sessionListener = createListenerMiddleware()

sessionListener.startListening({
    matcher: isAnyOf(setCredentials, logout),
    effect: (action, api) => {
        const prev = (api.getOriginalState() as { auth: AuthState }).auth
        api.dispatch(apiSlice.util.resetApiState())
        api.dispatch(v2Api.util.resetApiState())
        if (!setCredentials.match(action) || !isSameSession(prev, action.payload)) {
            api.dispatch(clearProject())
        }
    },
})

// Client-portal sessions are independent of the dashboard login: switching
// portal user (or signing out) drops only the portal's cached reports.
sessionListener.startListening({
    matcher: isAnyOf(portalSignedIn, portalSignedOut),
    effect: (_action, api) => {
        api.dispatch(portalApi.util.resetApiState())
    },
})
